import { MISSING_DEVICE_MARKER } from '@sb/integrations/implementations/gripper-general/steps/ActuateGripper/Arguments';

import { type ValidationRule } from '../../types';
import { createStepRule, diag } from '../../util/ruleHelpers';

const SOURCE_RULE = 'steps.ActuateGripper';

const CLEARED_MESSAGE =
  'Cannot reference the original equipment in this step. Please open the step to update the gripper selection.';

const DELETED_MESSAGE =
  'Referenced gripper has been removed from this robot. Update or remove the gripper selection.';

/**
 * Equipment-reference check for ActuateGripper steps. Mirrors the diagnostics
 * the remote-control import sanitizer (`checkEquipmentReferences`) produces,
 * but lives in the rule engine so it shares the engine's suppression handling
 * — an effectively-suppressed step is skipped before this rule runs.
 *
 * Intentionally function-style (not `CanonicalStepRuleBase`) so it adds only
 * the equipment-ref check without retroactively subjecting ActuateGripper
 * steps to runner-schema validation they were never checked against.
 */
export const ActuateGripperValidation: ValidationRule = createStepRule({
  id: SOURCE_RULE,
  stepKinds: ['ActuateGripper'],
  run: ({ routine, ctx, stepId }) => {
    const args = routine.stepConfigurations[stepId]?.args as
      | { deviceID?: string }
      | undefined;

    const deviceID = args?.deviceID;

    // No selection yet — nothing to validate (matches the sanitizer, which
    // only diagnoses a reference that exists but cannot be resolved).
    if (deviceID === undefined) return [];

    if (deviceID === MISSING_DEVICE_MARKER) {
      return [
        diag({
          code: 'ui.invalidConfiguration',
          message: CLEARED_MESSAGE,
          stepId,
          sourceRule: SOURCE_RULE,
          severity: 'error',
        }),
      ];
    }

    const exists = (ctx.equipment ?? []).some((eq) => eq.id === deviceID);

    if (!exists) {
      return [
        diag({
          code: 'ui.missingEquipment',
          message: DELETED_MESSAGE,
          stepId,
          sourceRule: SOURCE_RULE,
          severity: 'error',
        }),
      ];
    }

    return [];
  },
});
